<template>
    <!-- HOW THE PICTURE GETS CUT, and the preview is the control.
      --
      -- Two selects would have been enough to ship and would have been the
      -- wrong shape: the question "is 3 x 4 what I want" is answered by
      -- LOOKING at where the lines fall on this particular picture, not by
      -- reading two numbers. So the lines are drawn on the stage and the
      -- selects move them -- the same argument the meme rectangles record,
      -- where two buttons and an accept step were replaced by dragging the
      -- thing itself.
      --
      -- The count and the tile size are printed because they are the two
      -- facts somebody checks before pressing the button: a sprite sheet is
      -- right when the tiles come out the size of a frame. -->
    <div id="tile-panel" class="mt-4 flex flex-col gap-4">

        <div class="flex flex-wrap items-end gap-4">
            <label class="flex flex-col gap-1.5">
                <span class="text-[15px] font-bold">{{ t('tiles.across') }}</span>
                <select data-tile-cols v-model.number="uiStore.tileCols"
                        class="select select-sm rounded-lg w-24">
                    <option v-for="n in colChoices" :key="n" :value="n">{{ n }}</option>
                </select>
            </label>

            <label class="flex flex-col gap-1.5">
                <span class="text-[15px] font-bold">{{ t('tiles.down') }}</span>
                <select data-tile-rows v-model.number="uiStore.tileRows"
                        class="select select-sm rounded-lg w-24">
                    <option v-for="n in rowChoices" :key="n" :value="n">{{ n }}</option>
                </select>
            </label>

            <!-- THE SQUARE BUTTON, because that is the errand.
              -- Somebody cutting a picture for a profile grid wants n x n and
              -- has to set two selects to the same number to get it. One
              -- press, and it is also the only place the page says the word
              -- "square" -- which is what people call the thing they want. -->
            <button type="button" data-tile-square
                    class="btn btn-outline btn-sm rounded-lg"
                    @click="uiStore.tileRows = uiStore.tileCols">
                {{ t('tiles.square') }}
            </button>
        </div>

        <p data-tile-measure class="text-[13px] text-ink-mute leading-snug">
            {{ measure }}
        </p>

        <!-- A picture too small for the grid asked for is not a small set of
          -- tiles, it is empty files -- and an empty canvas is what a browser
          -- hands back rather than an error. Said here rather than on the
          -- press, because the selects are what caused it. -->
        <p v-if="tooFine" data-tile-toofine class="text-[13px] text-error leading-snug">
            {{ t('tiles.tooFine') }}
        </p>
    </div>
</template>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"
import { tileGrid, tilesFit, MAX_TILES_PER_SIDE } from "./../lib/tileSplit"

const props = defineProps({
    // The staged picture's own size, read off the loaded <img> by FilePreview.
    // Passed in rather than read from the store so this component has one
    // source for the geometry and cannot disagree with the overlay drawn on
    // the stage from the same numbers.
    width: {type: Number, default: 0},
    height: {type: Number, default: 0}
})

const { t } = useI18n()
const uiStore = useUiElements()

/* The choices are bounded BY THE PICTURE, not by a constant.
 *
 * Offering 12 columns for a 40px-wide sprite sheet is offering 12 empty
 * files. The cap is the smaller of the picture's own pixels and the
 * per-side limit in lib/tileSplit.js, so the select cannot be set to
 * something tilesFit() will then refuse.
 */
function upTo(px) {
    const max = Math.max(1, Math.min(MAX_TILES_PER_SIDE, px || MAX_TILES_PER_SIDE))
    return Array.from({length: max}, (_, i) => i + 1)
}
const colChoices = computed(() => upTo(props.width))
const rowChoices = computed(() => upTo(props.height))

const tooFine = computed(() =>
    props.width > 0 && props.height > 0
    && !tilesFit(props.width, props.height, uiStore.tileCols, uiStore.tileRows))

/* What comes out, in the two numbers somebody checks.
 *
 * The tile size is the FIRST tile's, and the sentence says "about" because
 * they differ by up to a pixel -- tileEdges() rounds each boundary rather
 * than accumulating a width, so 1080 into 7 is six 154s and a 156 rather
 * than seven 154s and three lost pixels. Printing one exact number for all
 * of them would be a figure that is wrong for most of them.
 */
const measure = computed(() => {
    const n = uiStore.tileCols * uiStore.tileRows
    if (!(props.width > 0 && props.height > 0) || tooFine.value) {
        return t("tiles.count", {n})
    }
    const first = tileGrid({
        width: props.width, height: props.height,
        cols: uiStore.tileCols, rows: uiStore.tileRows
    })[0]
    if (!first) {
        return t("tiles.count", {n})
    }
    return t("tiles.measure", {n, w: first.w, h: first.h})
})
</script>
